/**
 * @file ImageHeightField.tsx
 * @description Высота фото в колонке (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 §3 и «Дополнение» §3; данные db-design-sb120-stage1 §1,
 * решение (б) — умолчание «По фото»):
 *
 * ```
 * Высота   [По фото | По колонке | Числом]
 *   По колонке: не меньше ├─●─┤ [ 256 ] px   «Колонке с фото поставьте «Растянуть»»
 *   Числом: [ 480 ] px · на телефоне ↺ ×доля
 * ```
 *
 * «По фото» — пропорции файла или своего кадра, фото целиком; выбор «По
 * фото» снимает свои пропорции (`aspect`). «По колонке» — прежнее
 * «Заполнить колонку» (256 px), фото обрезается вокруг точки фокуса.
 */

import { DEFAULT_SITE_THEME, SECTION_LOOK_LIMITS, type SectionElementOf, type SectionImageHeight } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { FieldHint } from "../../../ui/settings-groups";
import { Segmented } from "../../../site-view/Segmented";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";
import { ownValue } from "../../../../lib/site-fields/number-input";

type Image = SectionElementOf<"image">;

const MODES = [
  { value: "photo" as const, label: "По фото" },
  { value: "column" as const, label: "По колонке" },
  { value: "number" as const, label: "Числом" },
];

const { imageHeight: H } = SECTION_LOOK_LIMITS;
/** Высота «Числом» при выборе режима. */
const NUMBER_DEFAULT = 480;

interface Props {
  element: Image;
  path: string;
  onChange: (element: Image) => void;
}

export function ImageHeightField({ element, path, onChange }: Props) {
  const { preview, theme } = useSectionForm();
  const factor = (theme ?? DEFAULT_SITE_THEME).spacing.phoneFactor;
  const height = element.height;
  const setHeight = (next: SectionImageHeight) => onChange({ ...element, height: next });

  function pick(kind: SectionImageHeight["kind"]) {
    if (kind === height.kind) return;
    if (kind === "photo") {
      const next = { ...element, height: { kind: "photo" as const } };
      delete next.aspect;
      onChange(next);
    } else setHeight(kind === "column" ? { kind: "column" } : { kind: "number", value: NUMBER_DEFAULT });
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Label>Высота</Label>
      <Segmented aria-label="Высота фото" options={MODES} value={height.kind} onChange={pick} />
      {height.kind === "column" ? (
        <>
          <NumberSliderField
            label="Не меньше"
            value={height.min}
            inherit={{ value: H.columnDefault, label: `${H.columnDefault} px` }}
            min={H.min}
            max={H.max}
            step={1}
            largeStep={16}
            unit="px"
            display={{ factor: 1, decimals: 2 }}
            onChange={(min) => setHeight(min === undefined ? { kind: "column" } : { kind: "column", min })}
            onPreview={(v) => preview(`${path}.height.min`, v)}
          />
          <FieldHint className="mt-0">Фото тянется на высоту ряда. Колонке с фото поставьте «Растянуть» (группа «Колонки»).</FieldHint>
        </>
      ) : null}
      {height.kind === "number" ? (
        <>
          <NumberSliderField
            label="Высота на ПК"
            value={ownValue(height.value, NUMBER_DEFAULT)}
            inherit={{ value: NUMBER_DEFAULT, label: `${NUMBER_DEFAULT} px` }}
            min={H.min}
            max={H.max}
            step={1}
            largeStep={16}
            unit="px"
            display={{ factor: 1, decimals: 2 }}
            onChange={(v) => setHeight({ ...height, value: v ?? NUMBER_DEFAULT })}
            onPreview={(v) => preview(`${path}.height.value`, v ?? NUMBER_DEFAULT)}
          />
          <NumberSliderField
            label="Высота на телефоне"
            value={height.valuePhone}
            inherit={{ value: Math.round(height.value * factor), label: `×${String(factor).replace(".", ",")}` }}
            min={H.min}
            max={H.max}
            step={1}
            unit="px"
            display={{ factor: 1, decimals: 2 }}
            onChange={(valuePhone) => {
              const next = { ...height, valuePhone };
              if (valuePhone === undefined) delete next.valuePhone;
              setHeight(next);
            }}
            onPreview={(v) => preview(`${path}.height.valuePhone`, v)}
          />
        </>
      ) : null}
    </div>
  );
}
